import React, { useMemo, useState } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
interface GanttItem {
  id: string | number;
  name: string;
  startDate: string;
  endDate: string;
  state?: string;
  status?: string;
  goal?: string;
  progress?: number;
}
interface GanttChartArtifactProps {
  data: GanttItem[];
}
const STATE_COLORS: Record<string, {
  bar: string;
  bg: string;
  text: string;
  border: string;
}> = {
  active: {
    bar: 'bg-indigo-500',
    bg: 'bg-indigo-500/10',
    text: 'text-indigo-400',
    border: 'border-indigo-500/30'
  },
  closed: {
    bar: 'bg-emerald-500',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30'
  },
  future: {
    bar: 'bg-slate-600',
    bg: 'bg-slate-600/10',
    text: 'text-slate-400',
    border: 'border-slate-600/30'
  },
  done: {
    bar: 'bg-emerald-500',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30'
  },
  'in progress': {
    bar: 'bg-blue-500',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500/30'
  },
  'to do': {
    bar: 'bg-slate-600',
    bg: 'bg-slate-600/10',
    text: 'text-slate-400',
    border: 'border-slate-600/30'
  }
};
const DEFAULT_COLOR = {
  bar: 'bg-indigo-500',
  bg: 'bg-indigo-500/10',
  text: 'text-indigo-400',
  border: 'border-indigo-500/30'
};
function getColor(state: string) {
  const key = state?.toLowerCase() || '';
  return STATE_COLORS[key] || DEFAULT_COLOR;
}
function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short'
    });
  } catch {
    return dateStr;
  }
}
function daysBetween(start: Date, end: Date): number {
  return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}
function generateMonthMarkers(timelineStart: Date, timelineEnd: Date): {
  label: string;
  position: number;
}[] {
  const markers: {
    label: string;
    position: number;
  }[] = [];
  const totalDays = daysBetween(timelineStart, timelineEnd);
  const current = new Date(timelineStart);
  current.setDate(1);
  if (current < timelineStart) current.setMonth(current.getMonth() + 1);
  while (current <= timelineEnd) {
    const daysFromStart = daysBetween(timelineStart, current);
    const position = daysFromStart / totalDays * 100;
    markers.push({
      label: current.toLocaleDateString('es-AR', {
        month: 'short',
        year: '2-digit'
      }),
      position: Math.min(position, 100)
    });
    current.setMonth(current.getMonth() + 1);
  }
  return markers;
}
export function GanttChartArtifact({
  data
}: GanttChartArtifactProps) {
  const [hoveredItem, setHoveredItem] = useState<string | number | null>(null);
  const items = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(item => item.startDate && item.endDate).map(item => ({
      ...item,
      start: new Date(item.startDate),
      end: new Date(item.endDate),
      state: item.state || item.status || 'active'
    })).sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [data]);
  const {
    timelineStart,
    timelineEnd,
    totalDays
  } = useMemo(() => {
    if (items.length === 0) return {
      timelineStart: new Date(),
      timelineEnd: new Date(),
      totalDays: 1
    };
    const allStarts = items.map(i => i.start.getTime());
    const allEnds = items.map(i => i.end.getTime());
    const minDate = new Date(Math.min(...allStarts));
    const maxDate = new Date(Math.max(...allEnds));
    // Add 5% padding on each side
    const range = maxDate.getTime() - minDate.getTime();
    const padding = range * 0.05;
    const start = new Date(minDate.getTime() - padding);
    const end = new Date(maxDate.getTime() + padding);
    return {
      timelineStart: start,
      timelineEnd: end,
      totalDays: daysBetween(start, end)
    };
  }, [items]);
  const monthMarkers = useMemo(() => generateMonthMarkers(timelineStart, timelineEnd), [timelineStart, timelineEnd]);
  const todayPosition = useMemo(() => {
    const today = new Date();
    if (today < timelineStart || today > timelineEnd) return null;
    return daysBetween(timelineStart, today) / totalDays * 100;
  }, [timelineStart, timelineEnd, totalDays]);
  if (items.length === 0) {
    return <div className="p-6 text-center text-slate-500 text-sm" data-id="element-135">
        No hay items con fechas para mostrar en el Gantt.
      </div>;
  }
  return <div className="p-4 bg-slate-900" data-id="element-136">
      {/* Header stats */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-800" data-id="element-137">
        <div className="flex items-center gap-2" data-id="element-138">
          <Calendar size={16} className="text-indigo-400" data-id="element-139" />
          <span className="text-sm font-bold text-slate-200" data-id="element-140">Timeline</span>
          <span className="text-xs text-slate-500" data-id="element-141">
            {formatDate(timelineStart.toISOString())} —{' '}
            {formatDate(timelineEnd.toISOString())}
          </span>
        </div>
        <div className="flex items-center gap-4" data-id="element-142">
          {Object.entries(STATE_COLORS).slice(0, 3).map(([state, colors]) => <div key={state} className="flex items-center gap-1.5" data-id="element-143">
                <div className={`w-2.5 h-2.5 rounded-sm ${colors.bar}`} data-id="element-144" />
                <span className="text-[10px] text-slate-400 capitalize" data-id="element-145">
                  {state}
                </span>
              </div>)}
        </div>
      </div>

      {/* Gantt chart */}
      <div className="relative" data-id="element-146">
        {/* Month markers header */}
        <div className="flex items-end h-8 mb-1 ml-48 relative border-b border-slate-800/50" data-id="element-147">
          {monthMarkers.map((marker, idx) => <div key={idx} className="absolute bottom-0 flex flex-col items-center" style={{
          left: `${marker.position}%`
        }} data-id="element-148">
              <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider whitespace-nowrap" data-id="element-149">
                {marker.label}
              </span>
              <div className="w-px h-2 bg-slate-700 mt-0.5" data-id="element-150" />
            </div>)}
        </div>

        {/* Rows */}
        <div className="space-y-1.5" data-id="element-151">
          {items.map((item, idx) => {
          const startOffset = daysBetween(timelineStart, item.start) / totalDays * 100;
          const duration = daysBetween(item.start, item.end) / totalDays * 100;
          const color = getColor(item.state);
          const isHovered = hoveredItem === item.id;
          const durationDays = daysBetween(item.start, item.end);
          return <div key={item.id || idx} className="flex items-center gap-0 group" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} data-id="element-152">
                {/* Label */}
                <div className="w-48 flex-shrink-0 pr-3" data-id="element-153">
                  <div className="flex items-center gap-2" data-id="element-154">
                    <div className={`w-1.5 h-8 rounded-full ${color.bar} flex-shrink-0`} data-id="element-155" />

                    <div className="min-w-0" data-id="element-156">
                      <p className={`text-xs font-semibold truncate transition-colors ${isHovered ? 'text-slate-100' : 'text-slate-300'}`} title={item.name} data-id="element-157">
                        {item.name}
                      </p>
                      <p className="text-[10px] text-slate-600 truncate" data-id="element-158">
                        {formatDate(item.startDate)} —{' '}
                        {formatDate(item.endDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bar area */}
                <div className="flex-1 relative h-10 bg-slate-950/50 rounded-md border border-slate-800/30 overflow-hidden" data-id="element-159">
                  {/* Grid lines for months */}
                  {monthMarkers.map((marker, mIdx) => <div key={mIdx} className="absolute top-0 bottom-0 w-px bg-slate-800/40" style={{
                left: `${marker.position}%`
              }} data-id="element-160" />)}

                  {/* Today marker */}
                  {todayPosition !== null && <div className="absolute top-0 bottom-0 w-0.5 bg-red-500/60 z-10" style={{
                left: `${todayPosition}%`
              }} data-id="element-161">
                      <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full" data-id="element-162" />
                    </div>}

                  {/* The bar */}
                  <div className={`absolute top-1.5 bottom-1.5 rounded-md transition-all duration-200 ${color.bar} ${isHovered ? 'opacity-100 shadow-lg' : 'opacity-80'}`} style={{
                left: `${Math.max(0, startOffset)}%`,
                width: `${Math.min(duration, 100 - startOffset)}%`,
                minWidth: '8px'
              }} data-id="element-163">
                    {/* Progress overlay if available */}
                    {item.progress !== undefined && item.progress < 100 && <div className="absolute inset-0 rounded-md bg-black/30" style={{
                  left: `${item.progress}%`
                }} data-id="element-164" />}

                    {/* Bar label (only if bar is wide enough) */}
                    {duration > 8 && <div className="absolute inset-0 flex items-center px-2 overflow-hidden" data-id="element-165">
                        <span className="text-[10px] font-bold text-white/90 truncate drop-shadow-sm" data-id="element-166">
                          {durationDays}d
                        </span>
                      </div>}
                  </div>

                  {/* Hover tooltip */}
                  {isHovered && <div className="absolute z-20 -top-14 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 shadow-xl shadow-black/50 pointer-events-none whitespace-nowrap" style={{
                left: `${Math.max(0, startOffset)}%`,
                transform: 'translateX(-10%)'
              }} data-id="element-167">
                      <p className="text-xs font-bold text-slate-100" data-id="element-168">
                        {item.name}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5" data-id="element-169">
                        <span className="text-[10px] text-slate-400" data-id="element-170">
                          {formatDate(item.startDate)} →{' '}
                          {formatDate(item.endDate)}
                        </span>
                        <span className={`text-[10px] font-bold capitalize ${color.text}`} data-id="element-171">
                          {item.state}
                        </span>
                        <span className="text-[10px] text-slate-500" data-id="element-172">
                          {durationDays} días
                        </span>
                      </div>
                      {item.goal && <p className="text-[10px] text-slate-500 mt-0.5 max-w-[200px] truncate" data-id="element-173">
                          {item.goal}
                        </p>}
                    </div>}
                </div>
              </div>;
        })}
        </div>

        {/* Today label */}
        {todayPosition !== null && <div className="absolute bottom-0 z-10 flex flex-col items-center pointer-events-none" style={{
        left: `calc(12rem + ${todayPosition}% * (100% - 12rem) / 100%)`
      }} data-id="element-174">
            <span className="text-[9px] font-bold text-red-400 bg-red-950/80 px-1.5 py-0.5 rounded border border-red-900/50 mt-2" data-id="element-175">
              HOY
            </span>
          </div>}
      </div>

      {/* Summary footer */}
      <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-800/50" data-id="element-176">
        <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-177">
          <span data-id="element-178">{items.length} items</span>
          <span data-id="element-179">·</span>
          <span data-id="element-180">{totalDays} días de rango</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-600" data-id="element-181">
          <Clock size={10} data-id="element-182" />
          <span data-id="element-183">Generado automáticamente desde Jira</span>
        </div>
      </div>
    </div>;
}